import React from 'react';
import { View, Pressable } from 'react-native';
import { Text } from '@/components/ui/text';
import { Icon } from '@/components/ui/icon';
import {
  Link2,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
} from 'lucide-react-native';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { cn } from '@/lib/utils';
import type {
  WorkoutSupersetRecord,
  WorkoutExerciseRecord,
} from '~/powersync/generated/schema';
import { ChatExerciseRow } from './ChatExerciseRow';

export interface ChatSupersetGroupProps {
  superset: WorkoutSupersetRecord;
  className?: string;
}

/**
 * A "Superset" card (design izkig): link-icon header + ⋮ menu, then the
 * member exercises as collapsible interactive rows. Reused by both the superset
 * card and the full workout card.
 */
export const ChatSupersetGroup: React.FC<ChatSupersetGroupProps> = ({
  superset,
  className,
}) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();

  const { data: exercises } = useLivePowerSyncQuery<WorkoutExerciseRecord>(
    db.Workout.queries.findExercisesBySuperset(superset.id),
    [superset.id],
  );

  const { data: allSupersets } = useLivePowerSyncQuery<WorkoutSupersetRecord>(
    db.Workout.queries.findSupersetsWithExercises(superset.workoutId ?? ''),
    [superset.workoutId],
  );

  const deleteMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.deleteSuperset(powerSync, superset.id),
  });
  const moveUpMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveSupersetUp(powerSync, superset.id),
  });
  const moveDownMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveSupersetDown(powerSync, superset.id),
  });

  const { dispatchConfirmationDialog } = useDeleteProtection(() =>
    deleteMutation.mutate(),
  );

  const isFirst = superset.sortIndex === 0;
  const isLast =
    !!allSupersets && superset.sortIndex === allSupersets.length - 1;

  return (
    <View className={cn('bg-surface rounded-lg p-4', className)}>
      <View className="flex-row items-center justify-between mb-1">
        <View className="flex-row items-center gap-2">
          <Icon as={Link2} size={14} className="text-text-secondary" />
          <Text className="text-sm font-medium text-text-secondary">
            {t('superset.label')}
          </Text>
        </View>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Pressable className="p-2 -m-2 opacity-70 hover:opacity-100">
              <Icon
                as={MoreVertical}
                size={16}
                className="text-text-secondary"
              />
            </Pressable>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onPress={() => moveUpMutation.mutate()}
              disabled={isFirst || moveUpMutation.isPending}
            >
              <Icon as={ChevronUp} size={16} />
              <Text>{t('superset.moveUp')}</Text>
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={() => moveDownMutation.mutate()}
              disabled={isLast || moveDownMutation.isPending}
            >
              <Icon as={ChevronDown} size={16} />
              <Text>{t('superset.moveDown')}</Text>
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={dispatchConfirmationDialog}
              disabled={deleteMutation.isPending}
              variant="destructive"
            >
              <Icon as={Trash2} size={16} className="text-destructive" />
              <Text>{t('superset.delete')}</Text>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </View>

      {exercises?.map((exercise) => (
        <ChatExerciseRow
          key={exercise.id}
          exerciseId={exercise.id}
          collapsible
          defaultExpanded={false}
          className="py-2"
        />
      ))}
    </View>
  );
};
